import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle } from '@material-ui/core';
import { Grid, Button, LinearProgress } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import generateId, { DbTypePrefix } from '../redux/generateId';
import { clearAllDataToSave, addDataToSave, dbSave } from '../redux/workBackendSlice';
import { DbKeyType, AllDbHash, loadAllDb } from '../redux/dataBackend';
import { FolderDb, addFolder } from '../redux/folderDbSlice';
import { BookDb, addBook } from '../redux/bookDbSlice';
import { SheetDb, addSheet } from '../redux/sheetDbSlice';
import { SheetInformationDb, addSheetInformation } from '../redux/sheetInformationDbSlice';
import { PaletteDbHashType, generatePaletteId, addPalette, PaletteDb } from '../redux/paletteDbSlice';
import { CardTemplateDbHashType, generateCardTemplateId, addCardTemplate, CardTemplateDb } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDbHashType, isImageAttributeTemplate, addCardTemplateAttribute, CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDbHashType, isCardTemplateImageShape, addCardTemplateShape, CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { CardDb, addCard } from '../redux/cardDbSlice';
import { CardAttributeDb, addCardAttribute } from '../redux/cardAttributeDbSlice';
import { CardShapeDb, addCardShape } from '../redux/cardShapeDbSlice';
import { MemoCardContentDb, addMemoCardContent } from '../redux/memoCardContentDbSlice';
import { UiCardItemDb, addUiCardItem } from '../redux/uiCardItemDbSlice';
import { DrawCardContentDb, addDrawCardContent } from '../redux/drawCardContentDbSlice';
import { UiCardSampleDataDb, addUiCardSampleData } from '../redux/uiCardSampleDataDbSlice';
import { CardCodeTemplateDb, addCardCodeTemplate } from '../redux/cardCodeTemplateDbSlice';
import { CardCodeDb, addCardCode } from '../redux/cardCodeDbSlice';
import { ArrowDb, addArrow } from '../redux/arrowDbSlice';
import { AlbumDb, addAlbum } from '../redux/albumDbSlice';
import { ImageDbHashType, generateImageId, addImage, ImageDb } from '../redux/imageDbSlice';
import { SheetTreeDb, addSheetTree } from '../redux/sheetTreeDbSlice';
import { RelationUserFolderDb, addRelationUserFolder } from '../redux/relationUserFolderDbSlice';
import { RelationUserBookDb, addRelationUserBook } from '../redux/relationUserBookDbSlice';
import { RelationUserPaletteDb, addRelationUserPalette } from '../redux/relationUserPaletteDbSlice';
import { RelationUserCardTemplateDb, addRelationUserCardTemplate } from '../redux/relationUserCardTemplateDbSlice';
import { RelationFolderBookDb, addRelationFolderBook } from '../redux/relationFolderBookDbSlice';
import { RelationPaletteCardTemplateDb, RelationPaletteCardTemplateDbHashType, addRelationPaletteCardTemplate } from '../redux/relationPaletteCardTemplateDbSlice';
import { RelationUserAlbumDb, addRelationUserAlbum } from '../redux/relationUserAlbumDbSlice';
import { RelationUserImageDb, addRelationUserImage } from '../redux/relationUserImageDbSlice';
import { RelationAlbumImageDb, addRelationAlbumImage } from '../redux/relationAlbumImageDbSlice';
import { JointBookDb, addJointBook } from '../redux/jointBookDbSlice';
import { PalettePermissionDb, addPalettePermission } from '../redux/palettePermissionDbSlice';
import { CardTemplatePermissionDb, addCardTemplatePermission } from '../redux/cardTemplatePermissionDbSlice';
import { JointPaletteDb, addJointPalette } from '../redux/jointPaletteDbSlice';
import { StatusJointPaletteDb, addStatusJointPalette } from '../redux/statusJointPaletteDbSlice';
import { StandbyPaletteJoinedDb, addStandbyPaletteJoined } from '../redux/standbyPaletteJoinedDbSlice';
import { StandbyCardTemplateJoinedDb, addStandbyCardTemplateJoined } from '../redux/standbyCardTemplateJoinedDbSlice';
import { StandbyRelationPaletteCardTemplateJoinedDb, addStandbyRelationPaletteCardTemplateJoined } from '../redux/standbyRelationPaletteCardTemplateJoinedDbSlice';
import { StandbyPalettePermissionJoinedDb, addStandbyPalettePermissionJoined } from '../redux/standbyPalettePermissionJoinedDbSlice';
import { StandbyCardTemplatePermissionJoinedDb, addStandbyCardTemplatePermissionJoined } from '../redux/standbyCardTemplatePermissionJoinedDbSlice';
import { NoteDb, addNote } from '../redux/noteDbSlice';
import { defaultBookCoverImage } from '../redux/preparedImages';

import useWorkBackend from '../hooks/useWorkBackend';
import useAllDbs from '../hooks/useAllDbs';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useAllFoldersDb from '../hooks/useAllFoldersDb';
import useAllPaletteDbs from '../hooks/useAllPaletteDbs';
import useAllAlbumDbs from '../hooks/useAllAlbumDbs';
import useImagesInAlbum from '../hooks/useImagesInAlbum';
import useStandardCardTemplateDb from '../hooks/useStandardCardTemplateDb';

import { deepCopy, getTimestamp, isCorrectKey } from '../general';

type Hash<T> = { [key in string]: T; }
type ConvertMethod = 'all' | 'palettesAndCardTemplates'
type AllDataType = AllDbHash & { userId: string }
type PalettesAndCardTemplatesImport = {
  palettes: PaletteDbHashType;
  cardTemplates: CardTemplateDbHashType;
  cardTemplateAttributes: CardTemplateAttributeDbHashType;
  cardTemplateShapes: CardTemplateShapeDbHashType;
  images: ImageDbHashType;
  relationPaletteCardTemplates: RelationPaletteCardTemplateDbHashType;
}

type DataConvertDialogProps = {
  open: boolean;
  onClose: () => void;
}
const DataConvertDialog: React.FC<DataConvertDialogProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const authUser = useWorkBackend().authUser;
  const userId = useCurrentUserId();
  const allDbs = useAllDbs(open);
  const folderLength = useAllFoldersDb(userId)[0].length;
  const paletteLength = useAllPaletteDbs(userId)[0].length;
  const [albumIds] = useAllAlbumDbs(userId);
  const albumLength = albumIds.length;
  const imageLength = useImagesInAlbum(albumIds[0]).length;
  const [memoId] = useStandardCardTemplateDb('memo');
  const [drawId] = useStandardCardTemplateDb('draw');
  const [uiId] = useStandardCardTemplateDb('ui');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'export' | 'import'>();

  type ConvertDataSets = { [key in DbKeyType]: {
    ids: string[];
    hash: { [key in string]: Object };
    prefix: DbTypePrefix;
    startLength?: number;
    res?: [string[], { [key in string]: Object }];
  }}

  const prefixToReferringKeys: { [key in DbTypePrefix]: string[] } = {
    'u': [],
    'f': [],
    'b': ['coverImage/imageId'],
    's': [],
    'si': ['sheetId'],
    'p': [],
    'ct': [],
    'cta': ['cardTemplateId', 'defaultImage/imageId'],
    'cts': ['cardTemplateId', 'image/imageId'],
    'c': ['sheetId', 'cardTemplateId', 'parentId', 'groupParent', 'links'],
    'ca': ['cardId', 'cardTamplateAttributeId', 'defaultImage/imageId', 'content/imageId'],
    'cs': ['cardId', 'cardTemplateShapeId', 'image/imageId'],
    'mcc': ['cardId'],
    'uci': ['cardId', 'parentId'],
    'dcc': ['cardId'],
    'ucsd': ['cardId', 'itemId'],
    'cct': ['cardTemplateId'],
    'cc': ['cardId'],
    'a': ['sheetId', 'fromId', 'toId', 'imageId', 'links'],
    'l': [],
    'ia': [],
    'i': [],
    'st': ['bookId', 'sheetId', 'parentSheetId'],
    'ruf': ['userId', 'folderId'],
    'rub': ['userId', 'bookId'],
    'rup': ['userId', 'paletteId'],
    'ruct': ['userId', 'cardTemplateId'],
    'rfb': ['userId', 'folderId', 'bookId'],
    'rpct': ['paletteId', 'cardTemplateId'],
    'rua': ['userId', 'albumId'],
    'rui': ['userId', 'imageId'],
    'rai': ['albumId', 'imageId'],
    'lct': [],
    'li': [],
    'jb': ['bookId', 'userId'],
    'pp': [],
    'ctp': [],
    'jp': [],
    'sjp': [],
    'spj': [],
    'sctj': [],
    'srpct': [],
    'sppj': [],
    'sctpj': [],
    'uc': [],
    'n': ['userId', 'referenceId']
  }

  const getTitle = () => {
    if (mode === 'export') return 'データをエクスポートする';
    else if (mode === 'import') return 'データをインポートする';
    else return 'データをエクスポート／インポートする';
  }
  const exit = () => {
    setLoading(false);
    setMode(undefined);
    onClose();
  }
  const exportAllData = () => {
    const json = JSON.stringify({ ...allDbs, userId });
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM
    // eslint-disable-next-line
    const blob = new Blob([bom, json], { type: 'application\/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    if (!a) return;
    a.download = 'magica_export_' + getTimestamp() + '.json';
    a.href = url;
    a.click();
    URL.revokeObjectURL(url); // メモリ解放

    exit();
  }
  const addData = (key: DbKeyType, id: string, value: Object) => {
    if (authUser && authUser !== 'noAuth') {
      addDataToSave(key, id, value);
      return;
    }
    if (key === 'folders') dispatch(addFolder({ id, ...value as FolderDb }));
    else if (key === 'books') dispatch(addBook({ id, ...value as BookDb }));
    else if (key === 'sheets') dispatch(addSheet({ id, ...value as SheetDb }));
    else if (key === 'sheetInformations') dispatch(addSheetInformation({ id, ...value as SheetInformationDb }));
    else if (key === 'palettes') dispatch(addPalette({ id, ...value as PaletteDb }));
    else if (key === 'cardTemplates') dispatch(addCardTemplate({ id, ...value as CardTemplateDb }));
    else if (key === 'cardTemplateAttributes') dispatch(addCardTemplateAttribute({ id, ...value as CardTemplateAttributeDb }));
    else if (key === 'cardTemplateShapes') dispatch(addCardTemplateShape({ id, ...value as CardTemplateShapeDb }));
    else if (key === 'cards') dispatch(addCard({ id, ...value as CardDb }));
    else if (key === 'cardAttributes') dispatch(addCardAttribute({ id, ...value as CardAttributeDb }));
    else if (key === 'cardShapes') dispatch(addCardShape({ id, ...value as CardShapeDb }));
    else if (key === 'memoCardContents') dispatch(addMemoCardContent({ id, ...value as MemoCardContentDb }));
    else if (key === 'uiCardItems') dispatch(addUiCardItem({ id, ...value as UiCardItemDb }));
    else if (key === 'drawCardContents') dispatch(addDrawCardContent({ id, ...value as DrawCardContentDb }));
    else if (key === 'uiCardSampleDatas') dispatch(addUiCardSampleData({ id, ...value as UiCardSampleDataDb }));
    else if (key === 'cardCodeTemplates') dispatch(addCardCodeTemplate({ id, ...value as CardCodeTemplateDb }));
    else if (key === 'cardCodes') dispatch(addCardCode({ id, ...value as CardCodeDb }));
    else if (key === 'arrows') dispatch(addArrow({ id, ...value as ArrowDb }));
    else if (key === 'albums') dispatch(addAlbum({ id, ...value as AlbumDb }));
    else if (key === 'images') dispatch(addImage({ id, ...value as ImageDb }));
    else if (key === 'sheetTrees') dispatch(addSheetTree({ id, ...value as SheetTreeDb }));
    else if (key === 'relationUserFolders') dispatch(addRelationUserFolder({ id, ...value as RelationUserFolderDb }));
    else if (key === 'relationUserBooks') dispatch(addRelationUserBook({ id, ...value as RelationUserBookDb }));
    else if (key === 'relationUserPalettes') dispatch(addRelationUserPalette({ id, ...value as RelationUserPaletteDb }));
    else if (key === 'relationUserCardTemplates') dispatch(addRelationUserCardTemplate({ id, ...value as RelationUserCardTemplateDb }));
    else if (key === 'relationFolderBooks') dispatch(addRelationFolderBook({ id, ...value as RelationFolderBookDb }));
    else if (key === 'relationPaletteCardTemplates') dispatch(addRelationPaletteCardTemplate({ id, ...value as RelationPaletteCardTemplateDb }));
    else if (key === 'relationUserAlbums') dispatch(addRelationUserAlbum({ id, ...value as RelationUserAlbumDb }));
    else if (key === 'relationUserImages') dispatch(addRelationUserImage({ id, ...value as RelationUserImageDb }));
    else if (key === 'relationAlbumImages') dispatch(addRelationAlbumImage({ id, ...value as RelationAlbumImageDb }));
    else if (key === 'jointBooks') dispatch(addJointBook({ id, ...value as JointBookDb }));
    else if (key === 'palettePermissions') dispatch(addPalettePermission({ id, ...value as PalettePermissionDb }));
    else if (key === 'cardTemplatePermissions') dispatch(addCardTemplatePermission({ id, ...value as CardTemplatePermissionDb }));
    else if (key === 'jointPalettes') dispatch(addJointPalette({ id, ...value as JointPaletteDb }));
    else if (key === 'statusJointPalettes') dispatch(addStatusJointPalette({ id, ...value as StatusJointPaletteDb }));
    else if (key === 'standbyPaletteJoineds') dispatch(addStandbyPaletteJoined({ id, ...value as StandbyPaletteJoinedDb }));
    else if (key === 'standbyCardTemplateJoineds') dispatch(addStandbyCardTemplateJoined({ id, ...value as StandbyCardTemplateJoinedDb }));
    else if (key === 'standbyRelationPaletteCardTemplateJoineds') dispatch(addStandbyRelationPaletteCardTemplateJoined({ id, ...value as StandbyRelationPaletteCardTemplateJoinedDb }));
    else if (key === 'standbyPalettePermissionJoineds') dispatch(addStandbyPalettePermissionJoined({ id, ...value as StandbyPalettePermissionJoinedDb }));
    else if (key === 'standbyCardTemplatePermissionJoineds') dispatch(addStandbyCardTemplatePermissionJoined({ id, ...value as StandbyCardTemplatePermissionJoinedDb }));
    else if (key === 'notes') dispatch(addNote({ id, ...value as NoteDb }));
  }
  const openUserFileSelector = (method: ConvertMethod) => {
    const ids: { [key in ConvertMethod]: string } = { 'all': 'all-import-file-selector', 'palettesAndCardTemplates': 'palettes-and-cardtemplates-import-file-selector' };
    const selector = document.getElementById(ids[method]);
    if (!selector) return;
    selector.click();
  }
  const importAllData = (obj: AllDataType) => {
    if (!userId) return;
    clearAllDataToSave();
    const { userId: oldId } = obj;

    const dataSets: ConvertDataSets = {
      users: { ids: Object.keys(obj.users), hash: obj.users, prefix: 'u' },
      folders: { ids: Object.keys(obj.folders), hash: obj.folders, prefix: 'f' },
      books: { ids: Object.keys(obj.books), hash: obj.books, prefix: 'b' },
      sheets: { ids: Object.keys(obj.sheets), hash: obj.sheets, prefix: 's' },
      sheetInformations: { ids: Object.keys(obj.sheetInformations), hash: obj.sheetInformations, prefix: 'si' },
      palettes: { ids: Object.keys(obj.palettes), hash: obj.palettes, prefix: 'p' },
      cardTemplates: { ids: Object.keys(obj.cardTemplates), hash: obj.cardTemplates, prefix: 'ct' },
      cardTemplateAttributes: { ids: Object.keys(obj.cardTemplateAttributes), hash: obj.cardTemplateAttributes, prefix: 'cta' },
      cardTemplateShapes: { ids: Object.keys(obj.cardTemplateShapes), hash: obj.cardTemplateShapes, prefix: 'cts' },
      cards: { ids: Object.keys(obj.cards), hash: obj.cards, prefix: 'c' },
      cardAttributes: { ids: Object.keys(obj.cardAttributes), hash: obj.cardAttributes, prefix: 'ca' },
      cardShapes: { ids: Object.keys(obj.cardShapes), hash: obj.cardShapes, prefix: 'cs' },
      memoCardContents: { ids: Object.keys(obj.memoCardContents), hash: obj.memoCardContents, prefix: 'mcc' },
      uiCardItems: { ids: Object.keys(obj.uiCardItems), hash: obj.uiCardItems, prefix: 'uci' },
      drawCardContents: { ids: Object.keys(obj.drawCardContents), hash: obj.drawCardContents, prefix: 'dcc' },
      uiCardSampleDatas: { ids: Object.keys(obj.uiCardSampleDatas), hash: obj.uiCardSampleDatas, prefix: 'ucsd' },
      cardCodeTemplates: { ids: Object.keys(obj.cardCodeTemplates), hash: obj.cardCodeTemplates, prefix: 'cct' },
      cardCodes: { ids: Object.keys(obj.cardCodes), hash: obj.cardCodes, prefix: 'cc' },
      arrows: { ids: Object.keys(obj.arrows), hash: obj.arrows, prefix: 'a' },
      albums: { ids: Object.keys(obj.albums), hash: obj.albums, prefix: 'ia' },
      images: { ids: Object.keys(obj.images), hash: obj.images, prefix: 'i' },
      sheetTrees: { ids: Object.keys(obj.sheetTrees), hash: obj.sheetTrees, prefix: 'st' },
      relationUserFolders: { ids: Object.keys(obj.relationUserFolders), hash: obj.relationUserFolders, prefix: 'ruf', startLength: folderLength },
      relationUserBooks: { ids: Object.keys(obj.relationUserBooks), hash: obj.relationUserBooks, prefix: 'rub' },
      relationUserPalettes: { ids: Object.keys(obj.relationUserPalettes), hash: obj.relationUserPalettes, prefix: 'rup', startLength: paletteLength },
      relationUserCardTemplates: { ids: Object.keys(obj.relationUserCardTemplates), hash: obj.relationUserCardTemplates, prefix: 'ruct' },
      relationFolderBooks: { ids: Object.keys(obj.relationFolderBooks), hash: obj.relationFolderBooks, prefix: 'rfb' },
      relationPaletteCardTemplates: { ids: Object.keys(obj.relationPaletteCardTemplates), hash: obj.relationPaletteCardTemplates, prefix: 'rpct' },
      relationUserAlbums: { ids: Object.keys(obj.relationUserAlbums), hash: obj.relationUserAlbums, prefix: 'rua', startLength: albumLength },
      relationUserImages: { ids: Object.keys(obj.relationUserImages), hash: obj.relationUserImages, prefix: 'rui' },
      relationAlbumImages: { ids: Object.keys(obj.relationAlbumImages), hash: obj.relationAlbumImages, prefix: 'rai' },
      latelyCardTemplates: { ids: Object.keys(obj.latelyCardTemplates), hash: obj.latelyCardTemplates, prefix: 'lct' },
      latelyImages: { ids: Object.keys(obj.latelyImages), hash: obj.latelyImages, prefix: 'li' },
      jointBooks: { ids: Object.keys(obj.jointBooks), hash: obj.jointBooks, prefix: 'jb' },
      palettePermissions: { ids: Object.keys(obj.palettePermissions), hash: obj.palettePermissions, prefix: 'pp' },
      cardTemplatePermissions: { ids: Object.keys(obj.cardTemplatePermissions), hash: obj.cardTemplatePermissions, prefix: 'ctp' },
      jointPalettes: { ids: Object.keys(obj.jointPalettes), hash: obj.jointPalettes, prefix: 'jp' },
      statusJointPalettes: { ids: Object.keys(obj.statusJointPalettes), hash: obj.statusJointPalettes, prefix: 'sjp' },
      standbyPaletteJoineds: { ids: Object.keys(obj.standbyPaletteJoineds), hash: obj.standbyPaletteJoineds, prefix: 'spj' },
      standbyCardTemplateJoineds: { ids: Object.keys(obj.standbyCardTemplateJoineds), hash: obj.standbyCardTemplateJoineds, prefix: 'sctj' },
      standbyRelationPaletteCardTemplateJoineds: { ids: Object.keys(obj.standbyRelationPaletteCardTemplateJoineds), hash: obj.standbyRelationPaletteCardTemplateJoineds, prefix: 'srpct' },
      standbyPalettePermissionJoineds: { ids: Object.keys(obj.standbyPalettePermissionJoineds), hash: obj.standbyPalettePermissionJoineds, prefix: 'sppj' },
      standbyCardTemplatePermissionJoineds: { ids: Object.keys(obj.standbyCardTemplatePermissionJoineds), hash: obj.standbyCardTemplatePermissionJoineds, prefix: 'sctpj' },
      userConditions: { ids: Object.keys(obj.userConditions), hash: obj.userConditions, prefix: 'uc' },
      notes: { ids: Object.keys(obj.notes), hash: obj.notes, prefix: 'n' }
    };
    const dataSetKeys = Object.keys(dataSets) as DbKeyType[];

    const replacedIds: { [key in string]: string; } = { [oldId]: userId };
    const exclusionIds: string[] = [];
    const exclusionPush = (id: string) => {if (!exclusionIds.includes(id)) exclusionIds.push(id);}
    Object.keys(obj.users).forEach(id => id !== oldId ? exclusionPush(id) : undefined)
    type StandardIdCheck = { nominee: string[]; checked?: string; }
    let oldStandardPalette: StandardIdCheck = { nominee: [] };
    let oldStandardPaletteOrder: number | undefined = undefined;
    type StandardCTIdsType = { memo: StandardIdCheck; draw: StandardIdCheck; ui: StandardIdCheck; }
    const standardCTIds: StandardCTIdsType = {
      memo: { nominee: [] },
      draw: { nominee: [] },
      ui: { nominee: [] }
    };
    let replacedDefaultBookImageId: string;

    const updateReference = <T extends Object>(value: T, prefix: DbTypePrefix): [T, 'inclusion' | 'exclusion'] => {
      let condition: 'inclusion' | 'exclusion' = 'inclusion';
      if (!prefixToReferringKeys[prefix] || prefixToReferringKeys[prefix].length === 0) return [value, 'inclusion'];

      const newValue = deepCopy(value);
      const keys = prefixToReferringKeys[prefix];
      keys.forEach(key => {
        const words = key.split('/');
        const props: Object[] = [newValue];
        words.forEach((word, ix) => {
          if (!isCorrectKey(props[ix], word)) return;
          if (ix === words.length - 1) {
            const target = props[ix][word];
            if (typeof target === 'string') {
              if (target === '') return;
              if (exclusionIds.includes(target)) {
                if (prefix === 'c') {
                  if (!standardCTIds.memo.checked && standardCTIds.memo.nominee.includes(target)) return;
                  else if (!standardCTIds.draw.checked && standardCTIds.draw.nominee.includes(target)) return;
                  else if (!standardCTIds.ui.checked && standardCTIds.ui.nominee.includes(target)) return;
                  else if (target === standardCTIds.memo.checked && memoId) props[ix][word] = memoId as any;
                  else if (target === standardCTIds.draw.checked && drawId) props[ix][word] = drawId as any;
                  else if (target === standardCTIds.ui.checked && uiId) props[ix][word] = uiId as any;
                  else condition = 'exclusion';
                } else if (prefix === 'rpct') {
                  if (!oldStandardPalette.checked && oldStandardPalette.nominee.includes(target)) return;
                } else if (prefix === 'b' && replacedDefaultBookImageId && target === replacedDefaultBookImageId) {
                  if ('coverImage' in newValue) newValue.coverImage = undefined;
                } else if (condition === 'inclusion') {
                  if (prefix === 'rup' && oldStandardPalette.nominee.includes(target)) oldStandardPalette.checked = target;
                  else if (prefix === 'ruct' && standardCTIds.memo.nominee.includes(target)) standardCTIds.memo.checked = target;
                  else if (prefix === 'ruct' && standardCTIds.draw.nominee.includes(target)) standardCTIds.draw.checked = target;
                  else if (prefix === 'ruct' && standardCTIds.ui.nominee.includes(target)) standardCTIds.ui.checked = target;
                  condition = 'exclusion';
                }
              } else if (condition === 'exclusion') {
                exclusionPush(target);
                if (isCorrectKey(replacedIds, target)) exclusionPush(replacedIds[target]);
              } else if (isCorrectKey(replacedIds, target)) props[ix][word] = replacedIds[target] as any;
            } else if (Array.isArray(target)) {
              let valid = true;
              const newArray: string[] = [];
              target.forEach(id => {
                if (exclusionIds.includes(id)) {
                  condition = 'exclusion';
                  valid = false;
                } else if (condition === 'exclusion') {
                  exclusionPush(id);
                  if (isCorrectKey(replacedIds, id)) exclusionPush(replacedIds[id]);
                  valid = false;
                } else if (isCorrectKey(replacedIds, id)) newArray.push(replacedIds[id]);
                else valid = false;
              })
              if (valid) props[ix][word] = newArray as any;
            }
          } else props.push(props[ix][word]);
        })
      })

      return [newValue, condition];
    }
    const replaceIdExe = <T extends Object>(ids: string[], hash: Hash<T>, prefix: DbTypePrefix): [string[], Hash<T>] => {
      const newIds: string[] = [];
      const res: Hash<T> = {};
      ids.forEach(id => {
        const value = hash[id];
        if ('standard' in value && value.standard) {
          if (prefix === 'p') oldStandardPalette.nominee.push(id);
          else if ('kind' in value && value.kind === 'memo') standardCTIds.memo.nominee.push(id);
          else if ('kind' in value && value.kind === 'draw') standardCTIds.draw.nominee.push(id);
          else if ('kind' in value && value.kind === 'ui') standardCTIds.ui.nominee.push(id);
          exclusionPush(id);
          return;
        }

        const newId = generateId(prefix);
        const [newValue, condition] = updateReference(value, prefix);

        if (prefix === 'rup' && 'order' in value && typeof value.order === 'number' &&
          'paletteId' in value && typeof value.paletteId === 'string' && value.paletteId === oldStandardPalette.checked
        ) oldStandardPaletteOrder = value.order;

        if (condition === 'exclusion') {
          exclusionPush(id);
          return;
        }
        newIds.push(newId);
        res[newId] = newValue;
        replacedIds[id] = newId;
      })
      return [newIds, res];
    }
    const convertData = <T extends Object>(ids: string[], hash: Hash<T>, prefix: DbTypePrefix): [[string[], Hash<T>], number] => {
      const newIds: string[] = [];
      const res: Hash<T> = {};
      let exclusionCount = 0;
      ids.forEach(id => {
        const value = deepCopy(hash[id]);
        if (exclusionIds.includes(id)) {
          exclusionCount++;
          return;
        }

        const [newValue, condition] = updateReference(value, prefix);

        if (condition === 'exclusion') {
          exclusionPush(id);
          exclusionCount++;
          return;
        } else if (prefix === 'i' && 'dataUrl' in value && value.dataUrl === defaultBookCoverImage) {
          replacedDefaultBookImageId = id;
          exclusionPush(id);
          exclusionCount++;
          return;
        }
        newIds.push(id);
        res[id] = newValue;
      })
      return [[newIds, res], exclusionCount];
    }
    const updateOrder = <T extends Object>(ids: string[], hash: Hash<T>, prefix: DbTypePrefix, startLength: number = 0): [string[], Hash<T>] => {
      const newIds: string[] = [];
      const res: Hash<T> = {};
      ids.forEach(id => {
        const value = deepCopy(hash[id]);
        const isTargetOfOrderChange = prefix === 'ruf' || prefix === 'rup' || prefix === 'rua';
        if (isTargetOfOrderChange && 'order' in value && typeof value.order === 'number') {
          if (prefix === 'rup' && oldStandardPaletteOrder !== undefined && value.order > oldStandardPaletteOrder) value.order += startLength - 1;
          else value.order += startLength;
        }
        newIds.push(id);
        res[id] = value;
      })
      return [newIds, res];
    }

    // replace all ids
    dataSetKeys.forEach(key => {
      if (key === 'users' || key === 'latelyCardTemplates' || key === 'latelyImages' || key === 'userConditions') return;
      const dataSet = dataSets[key];
      dataSet.res = replaceIdExe(dataSet.ids, dataSet.hash, dataSet.prefix);
    })

    // repeat converting data to be finished
    let halfway = true;
    do {
      halfway = false;
      dataSetKeys.forEach(key => {
        if (key === 'users' || key === 'latelyCardTemplates' || key === 'latelyImages' || key === 'userConditions') return;
        const dataSet = dataSets[key];
        if (!dataSet.res) return;
        let exclusionCount = 0;
        [dataSet.res, exclusionCount] = convertData(dataSet.res[0], dataSet.res[1], dataSet.prefix);
        if (exclusionCount > 0) halfway = true;
      })
    } while (halfway)

    // update order
    dataSetKeys.forEach(key => {
      const dataSet = dataSets[key];
      if (dataSet.res && dataSet.startLength) dataSet.res = updateOrder(dataSet.res[0], dataSet.res[1], dataSet.prefix, dataSet.startLength);
    })

    // Fix deleted RUI
    const RUIDataSetRes = dataSets['relationUserImages'].res;
    if (dataSets['images'].res) {
      dataSets['images'].res[0].forEach(id => {
        if (!RUIDataSetRes) return;
        const exists = RUIDataSetRes[0].find(rId => {
          const value = RUIDataSetRes[1][rId];
          return 'imageId' in value && value.imageId === id;
        });
        if (!exists) {
          const newId = generateId('rui');
          RUIDataSetRes[0].push(newId);
          RUIDataSetRes[1][newId] = { userId, imageId: id };
        }
      })
    }

    // execute data import
    dataSetKeys.forEach(key => {
      if (key === 'users' || key === 'latelyCardTemplates' || key === 'latelyImages' || key === 'userConditions') return;
      const res = dataSets[key].res;
      if (!res) return;
      res[0].forEach(id => addData(key, id, res[1][id]))
    })

    if (authUser && authUser !== 'noAuth') {
      const callback = () => {
        dispatch(loadAllDb());
        exit();
      }
      dbSave(false, callback);
    } else {
      dispatch(loadAllDb());
      exit();
    }
  }
  const importPalettesAndCardTemplatesSet = (obj: PalettesAndCardTemplatesImport) => {
    if (!userId) return;
    const { palettes, cardTemplates, cardTemplateAttributes, cardTemplateShapes, images, relationPaletteCardTemplates } = deepCopy(obj);
    const paletteIds = Object.keys(palettes);
    const cardTemplateIds = Object.keys(cardTemplates);
    const cardTemplateAttributeIds = Object.keys(cardTemplateAttributes);
    const imageIds = Object.keys(images);
    const cardTemplateShapeIds = Object.keys(cardTemplateShapes);
    const rpctIds = Object.keys(relationPaletteCardTemplates);
    const standardRpctIds: string[] = [];

    const rewritePaletteId = (oldId: string, newId: string, ids: string[], hash: RelationPaletteCardTemplateDbHashType) => {
      ids.forEach(targetId => {
        const value = hash[targetId];
        if (value.paletteId === oldId) value.paletteId = newId;
      })
    }
    const rewriteCardTemplateId = (oldId: string, newId: string, ids: string[], hash: RelationPaletteCardTemplateDbHashType | CardTemplateAttributeDbHashType | CardTemplateShapeDbHashType) => {
      ids.forEach(targetId => {
        const value = hash[targetId];
        if (value.cardTemplateId === oldId) value.cardTemplateId = newId;
      })
    }
    const rewriteImageId = (oldId: string, newId: string, ids: string[], hash: CardTemplateAttributeDbHashType | CardTemplateShapeDbHashType) => {
      ids.forEach(targetId => {
        const value = hash[targetId];
        if (isImageAttributeTemplate(value) && value.defaultImage.imageId === oldId) value.defaultImage.imageId = newId;
        else if (isCardTemplateImageShape(value) && value.image.imageId === oldId) value.image.imageId = newId;
      })
    }
    let paletteOrderPlus = 0;
    paletteIds.forEach(id => {
      const value = palettes[id];
      if (value.standard) {
        rpctIds.forEach(targetId => {if (relationPaletteCardTemplates[targetId].paletteId === id) standardRpctIds.push(targetId)})
        return;
      }
      const newId = generatePaletteId();
      rewritePaletteId(id, newId, rpctIds, relationPaletteCardTemplates);
      dispatch(addPalette({ id: newId, ...value }));
      dispatch(addRelationUserPalette({ userId, paletteId: newId, order: paletteLength + paletteOrderPlus }));
      paletteOrderPlus++;
    })
    cardTemplateIds.forEach(id => {
      const value = cardTemplates[id];
      if (value.standard) return;
      const newId = generateCardTemplateId();
      rewriteCardTemplateId(id, newId, rpctIds, relationPaletteCardTemplates);
      rewriteCardTemplateId(id, newId, cardTemplateAttributeIds, cardTemplateAttributes);
      rewriteCardTemplateId(id, newId, cardTemplateShapeIds, cardTemplateShapes);
      dispatch(addCardTemplate({ id: newId, ...value }));
      dispatch(addRelationUserCardTemplate({ userId, cardTemplateId: newId }));
    })
    let imageOrderPlus = 0;
    imageIds.forEach(id => {
      const value = images[id];
      if (value.dataUrl === defaultBookCoverImage) return;
      const newId = generateImageId();
      rewriteImageId(id, newId, cardTemplateAttributeIds, cardTemplateAttributes);
      rewriteImageId(id, newId, cardTemplateShapeIds, cardTemplateShapes);
      dispatch(addImage({ id: newId, ...value }));
      dispatch(addRelationUserImage({ userId, imageId: newId }));
      dispatch(addRelationAlbumImage({ albumId: albumIds[0], imageId: newId, order: imageLength + imageOrderPlus }));
    })

    rpctIds.forEach(id => {
      if (standardRpctIds.includes(id)) return;
      const value = relationPaletteCardTemplates[id];
      dispatch(addRelationPaletteCardTemplate(value));
    })
    cardTemplateAttributeIds.forEach(id => dispatch(addCardTemplateAttribute(cardTemplateAttributes[id])))
    cardTemplateShapeIds.forEach(id => dispatch(addCardTemplateShape(cardTemplateShapes[id])))

    exit();
  }
  const readFileInChunks = (file: File, method: ConvertMethod, chunkSize: number = 1024) => {
    const reader = new FileReader();
    let offset = 0;
    let result = '';

    // UTF-8 デコーダ（ストリーミングモードを使用）
    const decoder = new TextDecoder('utf-8', { fatal: false });

    reader.onload = (ev) => {
      const chunk = ev.target?.result as ArrayBuffer;

      // UTF-8 としてデコード（文字が途中でも次回に持ち越せる）
      result += decoder.decode(chunk, { stream: true });

      offset += chunkSize;
      if (offset < file.size) {
        readNextChunk();
      } else {
        // 最後のデコード（stream: false で flush する）
        result += decoder.decode();

        try {
          // BOM 除去（任意）
          const clean = result.replace(/^\uFEFF/, '');
          const parsed = JSON.parse(clean);

          if (method === 'palettesAndCardTemplates') {
            importPalettesAndCardTemplatesSet(parsed);
          } else {
            importAllData(parsed);
          }
        } catch (err) {
          console.error('JSON parse error:', err);
        }
      }
    };

    const readNextChunk = () => {
      const blob = file.slice(offset, offset + chunkSize);
      reader.readAsArrayBuffer(blob); // バイナリで読み込む
    };
    readNextChunk();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>, method: ConvertMethod) => {
    if (!ev.target.files) return;
    setLoading(true);
    const file = ev.target.files[0];
    readFileInChunks(file, method);
  }

  return (
    <Dialog open={open} onClose={exit} maxWidth="xl">
      <DialogTitle>{ getTitle() }</DialogTitle>
      <DialogContent>
        { loading && <LinearProgress /> }
        { !mode &&
          <>
            <Button color='primary' onClick={() => setMode('export')}>エクスポート</Button>
            <Button color='primary' onClick={() => setMode('import')}>インポート</Button>
          </>
        }
        { mode === 'export' &&
          <Button color='primary' onClick={exportAllData} disabled={loading}>
            全データをエクスポートする
          </Button>
        }
        { mode === 'import' &&
          <Grid container>
            <Grid item xs={12}>
              <Button color='primary' onClick={() => openUserFileSelector('all')} disabled={loading}>
                全データをインポートする
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button color='primary' onClick={() => openUserFileSelector('palettesAndCardTemplates')} disabled={loading}>
                パレットとカードテンプレートをセットでインポートする
              </Button>
            </Grid>
            <input type="file" accept=".json" id="palettes-and-cardtemplates-import-file-selector" onChange={(ev) => getFile(ev, 'palettesAndCardTemplates')} style={{display: 'none'}} />
            <input type="file" accept=".json" id="all-import-file-selector" onChange={(ev) => getFile(ev, 'all')} style={{display: 'none'}} />
          </Grid>
        }
      </DialogContent>
      <DialogActions>
        { mode &&
          <Button color='secondary' onClick={() => setMode(undefined)} disabled={loading}>戻る</Button>
        }
      </DialogActions>
    </Dialog>
  );
}

export default DataConvertDialog;
